import { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { Plus, Users, Download, Pencil, Trash2, Upload, FileSpreadsheet, FileText, Eye, X, Shield, Contact } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get auth headers from localStorage or sessionStorage
const getAuthHeaders = () => {
  const sessionToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
  return sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {};
};

// XHR-based API helper to avoid fetch body stream issues with Emergent platform
const apiRequest = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url);
    
    // Set headers
    const headers = { ...getAuthHeaders(), ...(options.headers || {}) };
    Object.keys(headers).forEach(key => {
      xhr.setRequestHeader(key, headers[key]);
    });
    
    xhr.onreadystatechange = () => {
      if (xhr.readyState === XMLHttpRequest.DONE) {
        let response;
        try {
          response = xhr.responseText ? JSON.parse(xhr.responseText) : {};
        } catch (e) {
          response = { detail: xhr.responseText || 'Request failed' };
        }
        
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({ ok: true, data: response, status: xhr.status });
        } else {
          resolve({ ok: false, data: response, status: xhr.status });
        }
      }
    };
    
    xhr.onerror = () => {
      reject(new Error('Network error'));
    };
    
    if (options.body) {
      xhr.send(options.body);
    } else {
      xhr.send();
    }
  });
};

export default function Referrals() {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [dietChartDialogOpen, setDietChartDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingDietChart, setUploadingDietChart] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const fileInputRef = useRef(null);
  const dietChartInputRef = useRef(null);
  const [contactImportDialogOpen, setContactImportDialogOpen] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [importingContacts, setImportingContacts] = useState(false);
  const [contactsSupported, setContactsSupported] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: ''
  });

  useEffect(() => {
    fetchReferrals();
    // Check if Contact Picker API is supported (Chrome Android yes, Safari iOS needs flag enabled)
    const checkContactsSupport = async () => {
      try {
        // Check if navigator.contacts exists and has the select method
        if ('contacts' in navigator && typeof navigator.contacts.select === 'function') {
          setContactsSupported(true);
        } else {
          // On iOS/Safari, show the button anyway but with instructions
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
          const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
          // Show button on mobile devices even if API not detected (for Safari users who might have it enabled)
          setContactsSupported(isMobile);
        }
      } catch (e) {
        setContactsSupported(false);
      }
    };
    checkContactsSupport();
  }, []);

  const fetchReferrals = async () => {
    try {
      const response = await fetch(`${API}/referrals`, {
        headers: getAuthHeaders(),
        
      });
      
      if (!response.ok) throw new Error('Failed to fetch referrals');
      
      const data = await response.json();
      setReferrals(data);
    } catch (error) {
      toast.error('Failed to load referrals');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Open phone's contact picker to select multiple contacts
  const openContactPicker = async () => {
    try {
      if (!('contacts' in navigator) || typeof navigator.contacts.select !== 'function') {
        // Check if iOS Safari
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        if (isIOS) {
          toast.error(
            'Safari requires Contact Picker to be enabled. Go to Settings → Safari → Advanced → Feature Flags → Contact Picker API',
            { duration: 8000 }
          );
        } else {
          toast.error('Contact picker is not supported on this browser. Please use Chrome on Android.');
        }
        return;
      }

      const props = ['name', 'email', 'tel'];
      const opts = { multiple: true };
      
      const contacts = await navigator.contacts.select(props, opts);
      
      if (contacts && contacts.length > 0) {
        // Process selected contacts
        const processedContacts = contacts.map((contact, index) => ({
          id: `contact_${Date.now()}_${index}`,
          name: contact.name?.[0] || '',
          email: contact.email?.[0] || '',
          phone: contact.tel?.[0] || '',
          selected: true
        })).filter(c => c.name || c.email || c.phone); // Filter out empty contacts
        
        if (processedContacts.length === 0) {
          toast.error('No valid contacts selected');
          return;
        }
        
        setSelectedContacts(processedContacts);
        setContactImportDialogOpen(true);
      }
    } catch (error) {
      console.error('Contact picker error:', error);
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (error.name === 'InvalidStateError') {
        toast.error('Please try again - contact picker was busy');
      } else if (error.name === 'SecurityError') {
        toast.error('Contact access was denied. Please allow access in Settings.');
      } else if (error.name === 'NotAllowedError') {
        toast.info('Contact selection was cancelled');
      } else {
        console.error('Contact picker error:', error);
        toast.error('Failed to access contacts');
      }
    }
  };

  // Toggle contact selection in the import dialog
  const toggleContactSelection = (contactId) => {
    setSelectedContacts(prev => 
      prev.map(c => c.id === contactId ? { ...c, selected: !c.selected } : c)
    );
  };

  // Import selected contacts as referrals
  const importSelectedContacts = async () => {
    const contactsToImport = selectedContacts.filter(c => c.selected && (c.email || c.phone));
    
    if (contactsToImport.length === 0) {
      toast.error('Please select at least one contact with email or phone');
      return;
    }
    
    setImportingContacts(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const contact of contactsToImport) {
      try {
        const result = await apiRequest(`${API}/referrals`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customer_name: contact.name || 'Unknown',
            customer_email: contact.email || '',
            customer_phone: contact.phone || ''
          })
        });
        
        if (result.ok) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (error) {
        failCount++;
      }
    }
    
    setImportingContacts(false);
    setContactImportDialogOpen(false);
    setSelectedContacts([]);
    
    if (successCount > 0) {
      toast.success(`Successfully imported ${successCount} referral${successCount > 1 ? 's' : ''}`);
      fetchReferrals();
    }
    if (failCount > 0) {
      toast.error(`Failed to import ${failCount} contact${failCount > 1 ? 's' : ''} (may already exist)`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate at least one identifier
    if (!formData.customer_email && !formData.customer_phone) {
      toast.error('Please provide either email or phone number');
      return;
    }
    
    try {
      const result = await apiRequest(`${API}/referrals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (!result.ok) {
        throw new Error(result.data.detail || 'Failed to create referral');
      }
      
      toast.success('Referral added successfully!');
      setDialogOpen(false);
      resetForm();
      fetchReferrals();
    } catch (error) {
      toast.error(error.message || 'Failed to add referral');
      console.error(error);
    }
  };

  const handleEdit = (referral) => {
    setSelectedReferral(referral);
    setFormData({
      customer_name: referral.customer_name,
      customer_email: referral.customer_email || '',
      customer_phone: referral.customer_phone || ''
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_email && !formData.customer_phone) {
      toast.error('Please provide either email or phone number');
      return;
    }
    
    try {
      const result = await apiRequest(`${API}/referrals/${selectedReferral.referral_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (!result.ok) {
        throw new Error(result.data.detail || 'Failed to update referral');
      }
      
      toast.success('Referral updated successfully!');
      setEditDialogOpen(false);
      resetForm();
      fetchReferrals();
    } catch (error) {
      toast.error(error.message || 'Failed to update referral');
      console.error(error);
    }
  };

  const handleDelete = async () => {
    try {
      const result = await apiRequest(`${API}/referrals/${selectedReferral.referral_id}`, {
        method: 'DELETE'
      });
      
      if (!result.ok) {
        throw new Error(result.data.detail || 'Failed to delete referral');
      }
      
      toast.success('Referral deleted successfully!');
      setDeleteDialogOpen(false);
      setSelectedReferral(null);
      fetchReferrals();
    } catch (error) {
      toast.error(error.message || 'Failed to delete referral');
      console.error(error);
    }
  };

  const resetForm = () => {
    setFormData({
      customer_name: '',
      customer_email: '',
      customer_phone: ''
    });
    setSelectedReferral(null);
  };

  const downloadTemplate = () => {
    const token = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
    window.open(`${API}/referrals/template?token=${token}`, '_blank');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    setUploading(true);
    try {
      const response = await fetch(`${API}/referrals/upload`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to upload referrals');
      }
      
      toast.success(data.message);
      
      // Show errors if any
      if (data.errors && data.errors.length > 0) {
        data.errors.slice(0, 5).forEach(err => toast.error(err));
        if (data.errors.length > 5) {
          toast.error(`...and ${data.errors.length - 5} more errors`);
        }
      }
      
      setUploadDialogOpen(false);
      fetchReferrals();
    } catch (error) {
      toast.error(error.message || 'Failed to upload referrals');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDietChartUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedReferral) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    setUploadingDietChart(true);
    try {
      const response = await fetch(`${API}/referrals/${selectedReferral.referral_id}/diet-chart`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload diet chart');
      }
      
      toast.success('Diet chart uploaded successfully');
      setDietChartDialogOpen(false);
      fetchReferrals();
    } catch (error) {
      toast.error(error.message || 'Failed to upload diet chart');
    } finally {
      setUploadingDietChart(false);
      if (dietChartInputRef.current) {
        dietChartInputRef.current.value = '';
      }
    }
  };

  const viewDietChart = (referral) => {
    const token = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
    window.open(`${API}/referrals/${referral.referral_id}/diet-chart?token=${token}`, '_blank');
  };

  const deleteDietChart = async (referral) => {
    if (!confirm('Are you sure you want to delete this diet chart?')) return;
    
    try {
      const response = await fetch(`${API}/referrals/${referral.referral_id}/diet-chart`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete diet chart');
      }
      
      toast.success('Diet chart deleted');
      fetchReferrals();
    } catch (error) {
      toast.error(error.message || 'Failed to delete diet chart');
    }
  };

  const openDietChartDialog = (referral) => {
    setSelectedReferral(referral);
    setDietChartDialogOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'active':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'pending':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'expired':
        return 'bg-gray-100 text-gray-500 border-gray-200';
      default:
        return 'bg-orange-100 text-orange-700 border-orange-200';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'paid':
        return 'Paid';
      case 'active':
        return 'Unpaid';  // Legacy - treat same as pending
      case 'pending':
        return 'Unpaid';
      case 'expired':
        return 'Expired';
      default:
        return 'Unpaid';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Important Notices */}
      <div className="space-y-3">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="text-amber-600 mt-0.5">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <p className="font-medium text-amber-800">Important Notice</p>
              <p className="text-sm text-amber-700 mt-1">
                For Cash on Delivery (COD) orders, commission will only be credited <strong>once payment is received</strong> and the order is marked as delivered.
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="text-blue-600 mt-0.5">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <p className="font-medium text-blue-800">Referral Policy</p>
              <p className="text-sm text-blue-700 mt-1">
                Referrals that have <strong>not made any purchase within 6 months</strong> of being added will be 
                automatically removed from the system. Please ensure your referrals complete their first order promptly.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Referrals
          </h1>
          <p className="text-muted-foreground text-sm lg:text-base">Manage your customer referrals</p>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          {/* Import from Contacts - only show on supported devices */}
          {contactsSupported && (
            <Button 
              onClick={openContactPicker}
              variant="outline"
              data-testid="import-contacts-button"
              className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
            >
              <Contact className="h-4 w-4 mr-2" />
              Import Contacts
            </Button>
          )}
          <Button 
            onClick={downloadTemplate}
            variant="outline"
            data-testid="download-template-button"
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Template
          </Button>
          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="upload-referrals-button">
                <Upload className="h-4 w-4 mr-2" />
                Bulk Upload
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Bulk Upload Referrals</DialogTitle>
                <DialogDescription>
                  Upload an Excel file with multiple referrals. Download the template first for the correct format.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600 mb-2">Select an Excel file (.xlsx)</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="referral-upload"
                  />
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading...' : 'Choose File'}
                  </Button>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Need the template?</span>
                  <Button variant="link" onClick={downloadTemplate} className="p-0 h-auto">
                    <FileSpreadsheet className="h-4 w-4 mr-1" />
                    Download Template
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-referral-button" size="lg">
                <Plus className="h-5 w-5 mr-2" />
                Add Referral
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Referral</DialogTitle>
                <DialogDescription>
                  Add customer details for commission tracking. Provide either email or phone number.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="customer_name">Customer Name *</Label>
                  <Input
                    id="customer_name"
                    data-testid="customer-name-input"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="customer_email">Customer Email</Label>
                  <Input
                    id="customer_email"
                    data-testid="customer-email-input"
                    type="email"
                    placeholder="john@example.com"
                    value={formData.customer_email}
                    onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="customer_phone">Customer Phone</Label>
                  <Input
                    id="customer_phone"
                    data-testid="customer-phone-input"
                    type="tel"
                    placeholder="+91 9876543210"
                    value={formData.customer_phone}
                    onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Provide either email or phone for matching Shopify orders
                  </p>
                </div>
                <Button data-testid="submit-referral-button" type="submit" className="w-full">Add Referral</Button>
              </form>
            </DialogContent>
          </Dialog>
          
          {/* Contact Import Dialog */}
          <Dialog open={contactImportDialogOpen} onOpenChange={setContactImportDialogOpen}>
            <DialogContent className="max-w-md max-h-[80vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Contact className="h-5 w-5 text-green-600" />
                  Import from Contacts
                </DialogTitle>
                <DialogDescription>
                  Select contacts to add as referrals. Only contacts with email or phone can be imported.
                </DialogDescription>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto space-y-2 py-2">
                {selectedContacts.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No contacts selected</p>
                ) : (
                  selectedContacts.map((contact) => (
                    <div 
                      key={contact.id}
                      onClick={() => toggleContactSelection(contact.id)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        contact.selected 
                          ? 'bg-green-50 border-green-300' 
                          : 'bg-gray-50 border-gray-200 opacity-60'
                      } ${!contact.email && !contact.phone ? 'opacity-40 cursor-not-allowed' : ''}`}
                    >
                      <div className="flex items-center gap-3">
                        <input 
                          type="checkbox" 
                          checked={contact.selected && (contact.email || contact.phone)}
                          disabled={!contact.email && !contact.phone}
                          onChange={() => {}}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{contact.name || 'Unknown'}</p>
                          {contact.email && (
                            <p className="text-sm text-muted-foreground truncate">{contact.email}</p>
                          )}
                          {contact.phone && (
                            <p className="text-sm text-muted-foreground truncate">{contact.phone}</p>
                          )}
                          {!contact.email && !contact.phone && (
                            <p className="text-xs text-red-500">No email or phone</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <DialogFooter className="flex-shrink-0 gap-2 sm:gap-0">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setContactImportDialogOpen(false);
                    setSelectedContacts([]);
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={importSelectedContacts}
                  disabled={importingContacts || selectedContacts.filter(c => c.selected && (c.email || c.phone)).length === 0}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {importingContacts ? (
                    <>Importing...</>
                  ) : (
                    <>Import {selectedContacts.filter(c => c.selected && (c.email || c.phone)).length} Contact{selectedContacts.filter(c => c.selected && (c.email || c.phone)).length !== 1 ? 's' : ''}</>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {referrals.length === 0 ? (
        <Card>
          <CardContent className="py-16">
            <div className="text-center">
              <Users className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-xl font-semibold mb-2">No referrals yet</h3>
              <p className="text-muted-foreground mb-6">Start adding customer referrals to earn commissions</p>
              <Button onClick={() => setDialogOpen(true)} data-testid="empty-add-referral-button">
                <Plus className="h-5 w-5 mr-2" />
                Add Your First Referral
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {referrals.map((referral, index) => (
            <motion.div
              key={referral.referral_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
            >
              <Card data-testid={`referral-card-${referral.referral_id}`} className="hover:shadow-md transition-all duration-200">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-lg">{referral.customer_name}</CardTitle>
                        {referral.auto_created && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium border bg-purple-100 text-purple-700 border-purple-200">
                            Auto
                          </span>
                        )}
                      </div>
                      {referral.customer_email && (
                        <p className="text-sm text-muted-foreground mt-1">{referral.customer_email}</p>
                      )}
                      {referral.customer_phone && (
                        <p className="text-sm text-muted-foreground mt-1">{referral.customer_phone}</p>
                      )}
                      {referral.auto_created_from_order && (
                        <p className="text-xs text-purple-600 mt-1">
                          Created from order {referral.auto_created_from_order}
                        </p>
                      )}
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(referral.status)}`}>
                      {getStatusLabel(referral.status)}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Commission Earned:</span>
                      <span className="font-semibold text-primary">₹{(referral.total_commission_earned || referral.commission_amount || 0).toFixed(2)}</span>
                    </div>
                    
                    {/* Diet Chart Section */}
                    <div className="flex justify-between items-center text-sm pt-2 border-t border-border">
                      <span className="text-muted-foreground">Diet Chart:</span>
                      {referral.diet_chart ? (
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-7 px-2 text-xs"
                            onClick={() => viewDietChart(referral)}
                            title="View diet chart"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            View
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                            onClick={() => deleteDietChart(referral)}
                            title="Delete diet chart"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-7 px-2 text-xs"
                          onClick={() => openDietChartDialog(referral)}
                        >
                          <Upload className="h-3 w-3 mr-1" />
                          Upload
                        </Button>
                      )}
                    </div>
                    
                    <div className="text-xs text-muted-foreground pt-2 border-t border-border">
                      Added {new Date(referral.created_at).toLocaleDateString()}
                      {referral.converted_at && (
                        <span className="ml-2">
                          • Converted {new Date(referral.converted_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    
                    {/* Show transaction history for converted referrals */}
                    {referral.transactions && referral.transactions.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-border">
                        <p className="text-xs font-medium text-muted-foreground mb-1">
                          Order History ({referral.transactions.length}):
                        </p>
                        <div className="space-y-1 max-h-24 overflow-y-auto">
                          {referral.transactions.map((txn, idx) => (
                            <div key={idx} className="text-xs flex justify-between items-center bg-muted/50 px-2 py-1 rounded">
                              <span>#{txn.order_number}</span>
                              <span className="font-medium">₹{txn.amount?.toLocaleString()}</span>
                              <span className="text-muted-foreground">
                                {txn.transaction_date ? new Date(txn.transaction_date).toLocaleDateString() : '-'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Edit/Delete buttons - only show for pending referrals */}
                    {referral.status === 'pending' && (
                      <div className="flex gap-2 pt-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => handleEdit(referral)}
                          data-testid={`edit-referral-${referral.referral_id}`}
                        >
                          <Pencil className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="text-destructive hover:text-destructive"
                          onClick={() => {
                            setSelectedReferral(referral);
                            setDeleteDialogOpen(true);
                          }}
                          data-testid={`delete-referral-${referral.referral_id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Confidentiality Notice */}
      <div className="mt-8 p-4 lg:p-6 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Shield className="h-5 w-5 lg:h-6 lg:w-6 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-800 text-sm lg:text-base mb-1">Data Privacy & Confidentiality</h4>
            <p className="text-blue-700 text-xs lg:text-sm leading-relaxed">
              Your referral data is completely confidential. The information you share about your referrals is not visible to the KiloBeaters team and there will be no attempt of any kind made to contact them directly. Your customer relationships are safe with us.
            </p>
          </div>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => {
        setEditDialogOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Referral</DialogTitle>
            <DialogDescription>
              Update customer details. Only pending referrals can be edited.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div>
              <Label htmlFor="edit_customer_name">Customer Name *</Label>
              <Input
                id="edit_customer_name"
                data-testid="edit-customer-name-input"
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_customer_email">Customer Email</Label>
              <Input
                id="edit_customer_email"
                data-testid="edit-customer-email-input"
                type="email"
                placeholder="john@example.com"
                value={formData.customer_email}
                onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit_customer_phone">Customer Phone</Label>
              <Input
                id="edit_customer_phone"
                data-testid="edit-customer-phone-input"
                type="tel"
                placeholder="+91 9876543210"
                value={formData.customer_phone}
                onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button data-testid="update-referral-button" type="submit">Update Referral</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Referral</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this referral? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {selectedReferral && (
            <div className="py-4">
              <p className="font-medium">{selectedReferral.customer_name}</p>
              <p className="text-sm text-muted-foreground">
                {selectedReferral.customer_email || selectedReferral.customer_phone}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-button">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diet Chart Upload Dialog */}
      <Dialog open={dietChartDialogOpen} onOpenChange={(open) => {
        setDietChartDialogOpen(open);
        if (!open) setSelectedReferral(null);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Diet Chart</DialogTitle>
            <DialogDescription>
              Upload a PDF diet chart for {selectedReferral?.customer_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <FileText className="h-8 w-8 mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-600 mb-2">Select a PDF file</p>
              <input
                ref={dietChartInputRef}
                type="file"
                accept=".pdf"
                onChange={handleDietChartUpload}
                className="hidden"
                id="diet-chart-upload"
              />
              <Button
                variant="outline"
                onClick={() => dietChartInputRef.current?.click()}
                disabled={uploadingDietChart}
                data-testid="select-diet-chart-button"
              >
                {uploadingDietChart ? 'Uploading...' : 'Choose PDF File'}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDietChartDialogOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
